import { useEffect, useState, useCallback } from "react";
import { api } from "../../../lib/apiClient";
import { ig, Avatar, Badge, Tag, IconBtn, PrimaryBtn, GhostBtn, Skeleton, timeAgo } from "./_inbox_shared";
import {
  Phone, MessageSquare, Mail, Calendar, NotebookPen, PlusCircle, MoreHorizontal,
  Edit2, X, Trash2, AlertCircle, Workflow, FileCheck, Clock,
} from "lucide-react";

const TABS = [
  { key: "details",       label: "Details" },
  { key: "fields",        label: "Fields" },
  { key: "tickets",       label: "Tickets" },
  { key: "opportunities", label: "Opportunities" },
  { key: "appointments",  label: "Appointments" },
  { key: "documents",     label: "Documents" },
  { key: "activity",      label: "Activity" },
  { key: "automation",    label: "Automation" },
  { key: "audit",         label: "Audit" },
];

export default function ContactPanel({ convId, reloadTick, onChanged }) {
  const [conv, setConv] = useState(null);
  const [contact, setContact] = useState(null);
  const [tab, setTab] = useState("details");

  const reload = useCallback(() => {
    if (!convId) return;
    api.get(`/api/inbox/conversations/${convId}`).then((d) => {
      setConv(d); setContact(d.contact);
    }).catch(() => {});
  }, [convId]);

  useEffect(() => { setConv(null); setContact(null); reload(); }, [convId, reload, reloadTick]);

  return (
    <aside style={{
      borderLeft: `1px solid ${ig.border}`, background: ig.card,
      display: "flex", flexDirection: "column", minWidth: 0, overflow: "hidden",
    }} data-testid="contact-panel">
      <div className="ig-scroll" style={{ flex: 1, overflowY: "auto" }}>
        <Header contact={contact} conv={conv} />
        <QuickActions contact={contact} />
        <Tabs tab={tab} setTab={setTab} />
        <div style={{ padding: "0 18px 22px" }}>
          {!contact ? <PanelLoading /> :
            tab === "details" ? <DetailsTab contact={contact} onSaved={() => { reload(); onChanged(); }} /> :
            tab === "fields"  ? <FieldsTab contact={contact} onSaved={() => { reload(); onChanged(); }} /> :
            tab === "tickets" ? <TicketsTab contact={contact} convId={convId} onChanged={() => { reload(); onChanged(); }} /> :
            tab === "opportunities" ? <ComingTab title="Opportunities"
                                                  blurb="Pipeline, stage, deal value, and rep — wired in the next pass." /> :
            tab === "appointments" ? <ComingTab title="Appointments"
                                                 blurb="Booked, confirmed, rescheduled, no-show — wired with the calendar integration." /> :
            tab === "documents" ? <ComingTab title="Documents"
                                              blurb="ID, paystubs, bank statements, credit report — secure portal upload coming next." /> :
            tab === "activity"  ? <ComingTab title="Activity"
                                              blurb="Forms, opens, clicks, calls, appointments, payments — combined view next." /> :
            tab === "automation" ? <ComingTab title="Automation"
                                               blurb="Active workflows, nurture sequences, AI status — controls land with the automation engine." /> :
            <ComingTab title="Audit"
                       blurb="Every action on this contact + conversation, with actor and timestamp — read-only audit log next." />}
        </div>
      </div>
    </aside>
  );
}

function PanelLoading() {
  return (
    <div style={{ display: "grid", gap: 10 }}>
      <Skeleton height={20} /><Skeleton height={50} radius={10} /><Skeleton height={50} radius={10} />
    </div>
  );
}

function Header({ contact, conv }) {
  if (!contact) {
    return <div style={{ padding: 18, borderBottom: `1px solid ${ig.borderSoft}` }}><Skeleton height={56} /></div>;
  }
  return (
    <div style={{ padding: 18, borderBottom: `1px solid ${ig.borderSoft}` }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <Avatar name={contact.full_name} size={48} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: ig.ink }}>{contact.full_name}</div>
          <div style={{ fontSize: 11.5, color: ig.muted, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis" }}>
            {contact.email}
          </div>
        </div>
        <IconBtn title="More"><MoreHorizontal size={14} /></IconBtn>
      </div>
      <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 11.5 }}>
        <KV k="Phone" v={contact.phone} />
        <KV k="State" v={contact.state || "—"} />
        <KV k="Source" v={contact.source} />
        <KV k="Language" v={(contact.language || "en").toUpperCase()} />
      </div>
      <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 5 }}>
        {(contact.tags || []).map((t) => <Tag key={t}>{t}</Tag>)}
      </div>
      {contact.dnd_status && (
        <div style={{ marginTop: 10, padding: 8, borderRadius: 8, background: "#FEE2E2", color: "#991B1B",
          border: "1px solid #FECACA", fontSize: 11, display: "flex", alignItems: "center", gap: 6 }}>
          <AlertCircle size={12} /> DND active — outbound SMS/WhatsApp blocked
        </div>
      )}
    </div>
  );
}

function QuickActions() {
  return (
    <div style={{ padding: "12px 14px", borderBottom: `1px solid ${ig.borderSoft}`, display: "grid",
      gridTemplateColumns: "repeat(5, 1fr)", gap: 6 }}>
      {[
        ["Call",     Phone,         "qa-call"],
        ["SMS",      MessageSquare, "qa-sms"],
        ["Email",    Mail,          "qa-email"],
        ["Book",     Calendar,      "qa-book"],
        ["Note",     NotebookPen,   "qa-note"],
      ].map(([label, Ic, tid]) => (
        <button key={label} data-testid={tid} style={{
          display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
          padding: "8px 0", border: `1px solid ${ig.border}`, borderRadius: 9,
          background: ig.card, cursor: "pointer", color: ig.ink, fontSize: 10.5, fontWeight: 700,
        }}>
          <Ic size={14} color={ig.emerald} />{label}
        </button>
      ))}
    </div>
  );
}

function Tabs({ tab, setTab }) {
  return (
    <div style={{
      position: "sticky", top: 0, zIndex: 4, background: ig.card,
      borderBottom: `1px solid ${ig.borderSoft}`, padding: "8px 12px", overflowX: "auto",
    }} className="ig-scroll">
      <div style={{ display: "flex", gap: 4 }}>
        {TABS.map((t) => (
          <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)} style={{
            padding: "6px 10px", borderRadius: 7, border: 0,
            background: t.key === tab ? ig.mint : "transparent",
            color: t.key === tab ? ig.forest : ig.muted,
            fontSize: 11.5, fontWeight: t.key === tab ? 800 : 600, cursor: "pointer", whiteSpace: "nowrap",
          }}>{t.label}</button>
        ))}
      </div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div>
      <div style={{ fontSize: 9.5, color: ig.muted, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase" }}>{k}</div>
      <div style={{ fontSize: 12, color: ig.ink, fontWeight: 600, marginTop: 1, wordBreak: "break-word" }}>{v ?? "—"}</div>
    </div>
  );
}

/* ─────────── DETAILS ─────────── */

function DetailsTab({ contact, onSaved }) {
  return (
    <div style={{ display: "grid", gap: 16, paddingTop: 14 }}>
      <Section title="Contact">
        <Row k="First name" v={contact.first_name} />
        <Row k="Last name"  v={contact.last_name} />
        <Row k="Email"      v={contact.email} />
        <Row k="Phone"      v={contact.phone} />
        <Row k="State / City" v={`${contact.state || "—"}, ${contact.city || ""}`} />
        <Row k="Source"     v={contact.source} />
        <Row k="Language"   v={(contact.language || "en").toUpperCase()} />
        <Row k="Owner"      v={contact.owner_id || "Unassigned"} />
        <Row k="Created"    v={timeAgo(contact.created_at) + " ago"} />
      </Section>
    </div>
  );
}

/* ─────────── FIELDS (funding & credit) ─────────── */

function FieldsTab({ contact, onSaved }) {
  return (
    <div style={{ display: "grid", gap: 16, paddingTop: 14 }}>
      <Section title="Funding goal">
        <Row k="Goal"            v={contact.funding_goal} />
        <Row k="Desired amount"  v={contact.desired_funding_amount ? `$${Number(contact.desired_funding_amount).toLocaleString()}` : "—"} />
        <Row k="Readiness"
             v={<Badge tone={readinessTone(contact.funding_readiness_status)}>{contact.funding_readiness_status || "—"}</Badge>} />
        <Row k="Application"     v={contact.application_status || "—"} />
        <Row k="Appointment"     v={contact.appointment_status || "—"} />
      </Section>
      <Section title="Credit profile">
        <Row k="Credit score"     v={contact.estimated_credit_score} />
        <Row k="Credit monitor"   v={contact.credit_monitoring_provider || "—"} />
      </Section>
      <Section title="Income & employment">
        <Row k="Employment"       v={contact.employment_status} />
        <Row k="Employer"         v={contact.employer_name || "—"} />
        <Row k="Job title"        v={contact.job_title || "—"} />
        <Row k="Time at job"      v={contact.time_at_job || "—"} />
        <Row k="Monthly income"   v={contact.monthly_income ? `$${Number(contact.monthly_income).toLocaleString()}` : "—"} />
      </Section>
      {contact.business_name && (
        <Section title="Business">
          <Row k="Business"       v={contact.business_name} />
          <Row k="Structure"      v={contact.business_structure} />
          <Row k="State"          v={contact.business_state} />
          <Row k="Revenue"        v={contact.business_revenue ? `$${Number(contact.business_revenue).toLocaleString()}` : "—"} />
        </Section>
      )}
    </div>
  );
}

function readinessTone(s) {
  if (!s) return "neutral";
  if (s.toLowerCase().includes("ready to apply") || s.toLowerCase().includes("approved")) return "success";
  if (s.toLowerCase().includes("denied")) return "error";
  if (s.toLowerCase().includes("almost")) return "warn";
  if (s.toLowerCase().includes("new")) return "info";
  return "info";
}

/* ─────────── TICKETS (full CRUD) ─────────── */

const TICKET_STATUSES   = ["new", "open", "pending_client", "in_review", "escalated", "resolved", "closed"];
const TICKET_PRIORITIES = ["low", "medium", "high", "urgent"];
const TICKET_CATEGORIES = ["Funding", "Credit Repair", "Billing", "Technical Support", "Document Upload",
                            "Application Review", "Appointment", "AI Conversation", "General Question"];

function TicketsTab({ contact, convId, onChanged }) {
  const [tickets, setTickets] = useState(null);
  const [editing, setEditing] = useState(null); // ticket object or "new"

  const load = () => api.get(`/api/inbox/tickets?contact_id=${contact.id}`).then((d) => setTickets(d.items));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [contact.id]);

  const startNew = () => setEditing({
    contact_id: contact.id, conversation_id: convId,
    title: "", description: "", status: "new", priority: "medium",
    category: "Funding", assigned_to: contact.owner_id || "",
  });

  return (
    <div style={{ paddingTop: 14 }} data-testid="tickets-tab">
      <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontSize: 11.5, color: ig.muted, fontWeight: 700, letterSpacing: 0.2, textTransform: "uppercase" }}>
          {tickets ? `${tickets.length} ticket${tickets.length === 1 ? "" : "s"}` : "Loading…"}
        </div>
        <div style={{ flex: 1 }} />
        <PrimaryBtn data-testid="ticket-new" onClick={startNew}><PlusCircle size={12} /> New ticket</PrimaryBtn>
      </div>

      {!tickets ? <Skeleton height={64} radius={10} /> :
        tickets.length === 0 ? (
          <Empty title="No tickets for this contact yet"
                 body="Create a ticket from a message when a client needs support or follow-up." />
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {tickets.map((t) => (
              <TicketCard key={t.id} t={t} onEdit={setEditing}
                onChanged={() => { load(); onChanged(); }} />
            ))}
          </div>
        )}

      {editing && (
        <TicketEditor t={editing} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); onChanged(); }} />
      )}
    </div>
  );
}

function TicketCard({ t, onEdit, onChanged }) {
  const remove = async () => {
    if (!window.confirm("Delete this ticket?")) return;
    await api.del(`/api/inbox/tickets/${t.id}`);
    onChanged();
  };
  return (
    <div style={{ padding: 12, borderRadius: 12, border: `1px solid ${ig.border}`,
      background: ig.card, boxShadow: ig.shadow }} data-testid={`ticket-${t.id}`}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: ig.ink }}>{t.title}</div>
          {t.description && (
            <div style={{ fontSize: 11.5, color: ig.muted, marginTop: 4, lineHeight: 1.5 }}>{t.description}</div>
          )}
        </div>
        <IconBtn title="Edit"   data-testid={`ticket-edit-${t.id}`}   onClick={() => onEdit(t)}><Edit2 size={12} /></IconBtn>
        <IconBtn title="Delete" data-testid={`ticket-delete-${t.id}`} danger onClick={remove}><Trash2 size={12} /></IconBtn>
      </div>
      <div style={{ marginTop: 9, display: "flex", flexWrap: "wrap", gap: 5, alignItems: "center" }}>
        <Badge tone={ticketStatusTone(t.status)}>{t.status.replace(/_/g, " ")}</Badge>
        <Badge tone={ticketPriorityTone(t.priority)}>{t.priority}</Badge>
        <Badge tone="neutral">{t.category}</Badge>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 10.5, color: ig.muted, display: "flex", alignItems: "center", gap: 4 }}>
          <Clock size={10} /> {timeAgo(t.updated_at)}
        </span>
      </div>
    </div>
  );
}

function ticketStatusTone(s) {
  return ({ new: "info", open: "info", pending_client: "warn", in_review: "info",
            escalated: "error", resolved: "success", closed: "neutral" }[s]) || "neutral";
}
function ticketPriorityTone(p) {
  return ({ low: "neutral", medium: "info", high: "warn", urgent: "error" }[p]) || "neutral";
}

function TicketEditor({ t, onClose, onSaved }) {
  const [form, setForm] = useState({ ...t });
  const [busy, setBusy] = useState(false);
  const isNew = !t.id;

  const save = async () => {
    if (!form.title.trim()) { alert("Title is required."); return; }
    setBusy(true);
    try {
      if (isNew) await api.post("/api/inbox/tickets", form);
      else await api.patch(`/api/inbox/tickets/${t.id}`, form);
      onSaved();
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(7,31,24,0.4)", zIndex: 50,
      display: "grid", placeItems: "center",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: 480, maxWidth: "92vw", maxHeight: "90vh", overflowY: "auto",
        background: ig.card, borderRadius: 16, boxShadow: ig.shadowLg, padding: 18,
      }} className="ig-fade-in ig-scroll" data-testid="ticket-editor">
        <div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
          <div style={{ width: 30, height: 30, borderRadius: 9, background: ig.mint,
            display: "grid", placeItems: "center", color: ig.emerald, marginRight: 10 }}>
            <FileCheck size={15} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 800 }}>{isNew ? "New ticket" : "Edit ticket"}</div>
          </div>
          <IconBtn onClick={onClose} title="Close"><X size={13} /></IconBtn>
        </div>

        <Field label="Title">
          <input data-testid="te-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            style={inputCss} placeholder="e.g. Credit review — denial follow-up" />
        </Field>
        <Field label="Description">
          <textarea data-testid="te-desc" value={form.description || ""}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={3} style={{ ...inputCss, minHeight: 70, padding: "9px 11px", fontFamily: "inherit" }}
            placeholder="What needs to happen?" />
        </Field>
        <Row2>
          <Field label="Status">
            <select data-testid="te-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
              style={inputCss}>{TICKET_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}</select>
          </Field>
          <Field label="Priority">
            <select data-testid="te-priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}
              style={inputCss}>{TICKET_PRIORITIES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
          </Field>
        </Row2>
        <Row2>
          <Field label="Category">
            <select data-testid="te-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              style={inputCss}>{TICKET_CATEGORIES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
          </Field>
          <Field label="Assigned to">
            <input data-testid="te-assigned" value={form.assigned_to || ""}
              onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
              style={inputCss} placeholder="agent-id or @name" />
          </Field>
        </Row2>

        <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <GhostBtn data-testid="te-cancel" onClick={onClose}>Cancel</GhostBtn>
          <PrimaryBtn data-testid="te-save" onClick={save} disabled={busy}>{busy ? "Saving…" : isNew ? "Create ticket" : "Save changes"}</PrimaryBtn>
        </div>
      </div>
    </div>
  );
}

const inputCss = {
  width: "100%", height: 34, padding: "0 11px", borderRadius: 9,
  border: `1px solid ${ig.border}`, background: ig.bg, fontSize: 12.5,
  outline: "none", color: ig.ink,
};

function Field({ label, children }) {
  return (
    <label style={{ display: "block", marginBottom: 10 }}>
      <div style={{ fontSize: 10, color: ig.muted, fontWeight: 800, letterSpacing: 0.4,
        textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
      {children}
    </label>
  );
}
function Row2({ children }) {
  return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>{children}</div>;
}

function Section({ title, children }) {
  return (
    <div>
      <div style={{ fontSize: 10.5, color: ig.muted, fontWeight: 800, letterSpacing: 0.4,
        textTransform: "uppercase", marginBottom: 6 }}>{title}</div>
      <div style={{ display: "grid", gap: 6 }}>{children}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "110px 1fr", gap: 12, padding: "5px 0",
      borderBottom: `1px dashed ${ig.borderSoft}` }}>
      <span style={{ fontSize: 11.5, color: ig.muted, fontWeight: 700 }}>{k}</span>
      <span style={{ fontSize: 12, color: ig.ink, fontWeight: 600, wordBreak: "break-word" }}>{v ?? "—"}</span>
    </div>
  );
}

function Empty({ title, body }) {
  return (
    <div style={{ textAlign: "center", padding: "32px 18px", color: ig.muted, border: `1px dashed ${ig.border}`, borderRadius: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: ig.ink }}>{title}</div>
      <div style={{ fontSize: 11.5, marginTop: 6, lineHeight: 1.5 }}>{body}</div>
    </div>
  );
}

function ComingTab({ title, blurb }) {
  return (
    <div style={{ paddingTop: 14 }}>
      <div style={{ padding: 18, borderRadius: 14, background: ig.mint, border: `1px solid #BFEED5` }}>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          <div style={{ width: 32, height: 32, borderRadius: 9, background: "#fff",
            display: "grid", placeItems: "center", color: ig.emerald }}><Workflow size={15} /></div>
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 800, color: ig.forest }}>{title}</div>
            <div style={{ fontSize: 11.5, color: ig.forest, marginTop: 4, lineHeight: 1.55 }}>{blurb}</div>
            <Badge tone="emerald" style={{ marginTop: 10 }}>Coming next phase</Badge>
          </div>
        </div>
      </div>
    </div>
  );
}
